import ConfirmationDialog from 'components/ReusableComponents/ConfirmationDialog'
import Datepicker from 'components/ReusableComponents/Datepicker'
import Dropdown from 'components/ReusableComponents/ManageHotel/Dropdown'
import TextInput from 'components/ReusableComponents/ManageHotel/TextInput'
import Toggle from 'components/ReusableComponents/Toggle'
import useForm from 'hooks/useForm'
import moment from 'moment'
import { useEffect, useState } from 'react'
import CreatableSelect from 'react-select/creatable'
import { useToasts } from 'react-toast-notifications'
import subsidiariesApi from 'services/api/subsidiaries'
import userApi from 'services/api/user'

import styles from './SwitchToPrepay.module.scss'

const SwitchToPrepayDialog = ({ onClose }) => {
  const [formData, handleFormChange, setFormData] = useForm({
    salesPersonUserId: '',
    referrerId: null,
    newReferrerName: null,
    monthlyRate: 0,
    quarterlyRate: 0,
    annualRate: 0,
    paymentPeriod: 'monthly',
    isAutoPayEnabled: false,
    serviceStartDate: moment().add(1, 'day').format('YYYY-MM-DD')
  })

  const { addToast } = useToasts()

  const [referrers, setReferrers] = useState()
  const [salesAgents, setSalesAgents] = useState([])

  useEffect(() => {
    userApi.getSalesAgents().then(res => {
      setSalesAgents(res)
    })
  }, [])

  useEffect(() => {
    subsidiariesApi.referrers.get().then(res => {
      setReferrers(res.map(r => ({ value: r.id, label: r.name })))
    })
  }, [])

  useEffect(() => {
    if (formData.paymentPeriod === 'monthly') {
      setFormData({
        isAutoPayEnabled: true
      })
    }
  }, [formData.paymentPeriod, setFormData])

  const handleChangeReferer = (newValue, actionMeta) => {
    if (actionMeta.action === 'create-option') {
      setFormData({ newReferrerName: newValue.value, referrerId: null })
      return
    }

    if (actionMeta.action === 'clear') {
      setFormData({ referrerId: null, newReferrerName: null })
      return
    }

    if (actionMeta.action === 'select-option') {
      setFormData({ referrerId: newValue.value, newReferrerName: null })
    }
  }

  function switchToPrepayHandler() {
    const { salesPersonUserId, monthlyRate, quarterlyRate, annualRate } = formData

    const annualRateNumber = Number(annualRate)
    const monthlyRateNumber = Number(monthlyRate)
    const quarterlyRateNumber = Number(quarterlyRate)

    if (!salesPersonUserId) {
      addToast('Please select sales agent', { appearance: 'error' })
      return
    }

    if (annualRateNumber === 0 && monthlyRateNumber === 0 && quarterlyRateNumber === 0) {
      addToast('Rates cannot be zero', { appearance: 'error' })
      return
    }

    if (quarterlyRateNumber > monthlyRateNumber) {
      addToast('Quarterly rate cannot be greater than monthly rate', {
        appearance: 'error'
      })
      return
    }

    if (annualRateNumber > monthlyRateNumber) {
      addToast('Annual rate cannot be greater than monthly rate', { appearance: 'error' })
      return
    }

    if (annualRateNumber > quarterlyRateNumber) {
      addToast('Annual rate cannot be greater than quarterly rate', {
        appearance: 'error'
      })
      return
    }

    if (salesPersonUserId) {
      const formDataWithoutEmptyFields = Object.keys(formData).reduce((acc, curr) => {
        if (
          formData[curr] !== '' &&
          formData[curr] !== null &&
          formData[curr] !== undefined
        ) {
          return { ...acc, [curr]: formData[curr] }
        }
        return acc
      }, {})

      subsidiariesApi
        .switchToPrepay(formDataWithoutEmptyFields)
        .then(res => {
          if (res) {
            addToast('Hotel switched to prepay successfully', { appearance: 'success' })
            onClose()
          }
        })
        .catch(err => {
          addToast(`Something went wrong: ${err.toString()}`, { appearance: 'error' })
        })
    } else {
      addToast('Please fill out all fields', { appearance: 'error' })
    }
  }

  return (
    <ConfirmationDialog
      title="Switch to prepay"
      maxWidth={800}
      theme="blacklist"
      contentPadding="0px"
      positiveMessage="Switch"
      negativeMessage="Cancel"
      onConfirm={switchToPrepayHandler}
      onClose={onClose}
    >
      <div className={styles.wrapper}>
        <div className={styles.formRow}>
          <Dropdown
            label="Sales agent"
            defaultVal={'Select sales agent'}
            options={salesAgents?.reduce(
              (acc, curr) => ({ ...acc, [curr.id]: curr.fullName }),
              {}
            )}
            onChange={handleFormChange}
            name="salesPersonUserId"
            value={formData.salesPersonUserId}
          />
          <label className={styles.creatableSelectContainer}>
            <div className={styles.label}>Select Referer</div>
            <CreatableSelect onChange={handleChangeReferer} options={referrers} />
          </label>
        </div>
        <div className={styles.serviceStartDate}>
          <div className={styles.label}>Service start date</div>
          <Datepicker
            className={styles.datepicker}
            minDate={moment().add(1, 'day').toDate()}
            maxDate={moment().add(1, 'month').endOf('month').toDate()}
            selected={
              formData.serviceStartDate
                ? moment(formData.serviceStartDate).toDate()
                : null
            }
            onChange={date =>
              setFormData({ serviceStartDate: moment(date).format('YYYY-MM-DD') })
            }
          />
        </div>
        <div className={styles.formRow}>
          <Dropdown
            label="Payment period"
            defaultVal="Monthly"
            options={{
              monthly: 'Monthly',
              quarterly: 'Quarterly',
              annual: 'Annual'
            }}
            name="paymentPeriod"
            onChange={handleFormChange}
            value={formData.paymentPeriod}
          />
          <Toggle
            className={styles.toggle}
            isGray={formData.paymentPeriod === 'monthly'}
            disabled={formData.paymentPeriod === 'monthly'}
            checked={formData.isAutoPayEnabled}
            label="Enable automatic payments"
            handleChange={() =>
              setFormData({ isAutoPayEnabled: !formData.isAutoPayEnabled })
            }
          />
        </div>
        <div className={styles.formRow}>
          <TextInput
            type="number"
            label="Monthly rate (USD / month)"
            value={formData.monthlyRate}
            onChange={e => setFormData({ monthlyRate: e.target.value })}
          />
          <TextInput
            type="number"
            label="Quarterly rate (USD / month)"
            value={formData.quarterlyRate}
            onChange={e => setFormData({ quarterlyRate: e.target.value })}
          />
          <TextInput
            type="number"
            label="Annual rate (USD / month)"
            value={formData.annualRate}
            onChange={e => setFormData({ annualRate: e.target.value })}
          />
        </div>
      </div>
    </ConfirmationDialog>
  )
}

export default SwitchToPrepayDialog
